import { t } from '@lingui/core/macro';
import { Fragment } from 'react';
import { z } from 'zod';

import { LogicButton } from '@/components/buttons/LogicButton';
import { withFieldGroup } from '@/components/Form';
import { FieldError } from '@/components/Form/fields';
import { cn } from '@/lib/utils';

import RuleBuilderCondition from './RuleBuilderCondition';
import RuleBuilderMultiCondition from './RuleBuilderMultiCondition';
import RuleEntityDropdown from './RuleEntityDropdown';
import type {
  AndConditionPath,
  ConditionGroupFieldValues,
  OrConditionPath,
  RuleBuilderAttributeType,
  RuleEntityType,
} from './types';
import { useAudienceRuleForm } from './types';
import { initializeCondition } from './util';

const MAX_AND_CONDITIONS = 5;

const buildAndConditionPath = (basePath: OrConditionPath, andIndex: number): AndConditionPath =>
  `${basePath}.andConditions[${andIndex}]`;

const RuleBuilderConditionGroup = withFieldGroup({
  defaultValues: {
    andConditions: [],
  } as ConditionGroupFieldValues,
  props: {} as {
    basePath: OrConditionPath;
    ruleEntities: RuleEntityType[];
    disabled?: boolean;
    onRemoveOrCondition: () => void;
  },
  render: function RuleBuilderConditionGroupRender({
    group,
    basePath,
    ruleEntities,
    disabled = false,
    onRemoveOrCondition,
  }) {
    const form = useAudienceRuleForm();

    const andConditionsSchema = z
      .array(z.any())
      .max(
        MAX_AND_CONDITIONS,
        t`You can only add up to ${MAX_AND_CONDITIONS} AND rules within a group`,
      );

    return (
      <group.Field
        name="andConditions"
        mode="array"
        validators={{
          onDynamic: andConditionsSchema,
        }}
      >
        {(andField) => (
          <div
            className={cn(
              'flex flex-1 flex-col items-start justify-center gap-4',
              'w-full rounded border border-gray-300 bg-gray-100',
              'px-6 pt-6 pr-4.5 pb-2',
              'max-lg:px-6 max-lg:pr-4.5',
              'max-sm:px-4 max-sm:pt-4.5 max-sm:pr-3 max-sm:pb-2',
            )}
          >
            {andField.state.value.map((condition, andIndex, allConditions) => {
              const ruleEntity = ruleEntities.find((entity) => entity.id === condition.object)!;
              const conditionPath = buildAndConditionPath(basePath, andIndex);

              return (
                <Fragment key={andIndex}>
                  {andIndex > 0 && <LogicButton value="AND" isReadOnly />}
                  {condition.object === 'company' ? (
                    <RuleBuilderCondition
                      form={form}
                      fields={conditionPath}
                      basePath={conditionPath}
                      ruleEntity={ruleEntity}
                      disabled={disabled}
                      onConditionRemove={() => {
                        andField.removeValue(andIndex);
                        if (allConditions.length === 1) {
                          onRemoveOrCondition();
                        }
                      }}
                    />
                  ) : (
                    <RuleBuilderMultiCondition
                      form={form}
                      fields={conditionPath}
                      basePath={conditionPath}
                      ruleEntity={ruleEntity}
                      object={condition.object}
                      existingAttributes={condition.attributes as RuleBuilderAttributeType[]}
                      disabled={disabled}
                      onConditionRemove={() => {
                        andField.removeValue(andIndex);
                        if (allConditions.length === 1) {
                          onRemoveOrCondition();
                        }
                      }}
                    />
                  )}
                </Fragment>
              );
            })}
            {andField.state.meta.errors.length > 0 && (
              <FieldError errors={andField.state.meta.errors} />
            )}
            <RuleEntityDropdown
              title={
                andField.state.value.length >= MAX_AND_CONDITIONS
                  ? t`You can only add up to ${MAX_AND_CONDITIONS} AND rules within a group`
                  : null
              }
              type="AND"
              ruleEntities={ruleEntities}
              disabled={andField.state.value.length >= MAX_AND_CONDITIONS || disabled}
              onSelect={(ruleEntity, attribute) =>
                andField.pushValue(initializeCondition(ruleEntity, attribute))
              }
            />
          </div>
        )}
      </group.Field>
    );
  },
});

export default RuleBuilderConditionGroup;
